import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { formatEuro } from "@/data/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaiementSucces() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("loading");
  const [recap, setRecap] = useState(null);

  useEffect(() => {
    document.title = "Confirmation de paiement — NBPerf";
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          const c = await axios.get(`${API}/orders/confirmation/${sessionId}`);
          setRecap(c.data.order);
          setState("paid");
          return;
        }
      } catch {
        // retry
      }
      if (++tries < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main className="pt-16 min-h-screen flex items-center" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-20 text-center w-full">
        {state === "loading" && (
          <>
            <Loader2 className="w-14 h-14 text-[#6EB000] mx-auto animate-spin" />
            <h1 className="font-display text-4xl text-white mt-8">Vérification du paiement…</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#6EB000] mx-auto" strokeWidth={1.4} />
            <h1 className="font-display text-4xl sm:text-6xl text-white mt-8" data-testid="payment-success-title">Paiement confirmé</h1>
            <p className="text-white/60 mt-4">
              Merci ! Votre demande est enregistrée. Nicolas vous recontacte rapidement pour planifier l'intervention.
            </p>
            {recap && (
              <div className="mt-10 bg-[#343A3D] border border-[#454C50] p-6 text-left space-y-3" data-testid="payment-success-recap">
                <div className="flex justify-between text-sm">
                  <span className="text-white/80">{recap.prestation.label}</span>
                  <span className="text-white">{recap.prestation.price_label}</span>
                </div>
                {recap.options.map((o) => (
                  <div key={o.id} className="flex justify-between text-sm">
                    <span className="text-white/50">+ {o.label}</span>
                    <span className="text-[#F5A623]">{o.price_label}</span>
                  </div>
                ))}
                <div className="border-t border-[#454C50] pt-3 flex justify-between items-center">
                  <span className="uppercase tracking-widest text-xs text-white/50">Montant payé</span>
                  <span className="font-display text-3xl text-[#6EB000]">{formatEuro(recap.amount_eur)}</span>
                </div>
                <p className="text-xs text-white/40">
                  {recap.vehicle.marque} {recap.vehicle.modele} ({recap.vehicle.annee}) · Réf. commande : <span className="font-mono">{recap.order_id}</span>
                  {recap.payment?.ref && <> · Réf. paiement : <span className="font-mono">{recap.payment.ref}</span></>}
                </p>
              </div>
            )}
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-14 h-14 text-[#F5A623] mx-auto" />
            <h1 className="font-display text-4xl text-white mt-8">Paiement en cours de traitement</h1>
            <p className="text-white/60 mt-4">Votre banque finalise la transaction. Vous recevrez la confirmation par NBPerf.</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 text-[#F5A623] mx-auto" />
            <h1 className="font-display text-4xl text-white mt-8">Lien de confirmation invalide</h1>
          </>
        )}
        <Link to="/" className="mt-10 inline-flex items-center gap-2 text-[#6EB000] text-sm uppercase tracking-widest hover:underline" data-testid="success-back-home">
          <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
        </Link>
      </div>
    </main>
  );
}

export function PaiementAnnule() {
  useEffect(() => {
    document.title = "Paiement annulé — NBPerf";
  }, []);
  return (
    <main className="pt-16 min-h-screen flex items-center" data-testid="payment-cancel-page">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-20 text-center">
        <XCircle className="w-16 h-16 text-[#F5A623] mx-auto" strokeWidth={1.4} />
        <h1 className="font-display text-4xl sm:text-6xl text-white mt-8">Paiement annulé</h1>
        <p className="text-white/60 mt-4">
          Aucun montant n'a été débité. Votre configuration est conservée : vous pouvez reprendre quand vous voulez.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/configurateur"
            className="clip-angle inline-flex items-center gap-2 bg-[#6EB000] text-black font-bold px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#5A9000] transition-colors"
            data-testid="cancel-retry-button"
          >
            Reprendre ma configuration
          </Link>
          <a href="tel:+33781901248" className="clip-angle inline-flex items-center gap-2 border border-[#6EB000] text-[#6EB000] px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#6EB000]/10 transition-colors" data-testid="cancel-phone-link">
            07 81 90 12 48
          </a>
        </div>
      </div>
    </main>
  );
}
